import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Form,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  message,
} from 'antd';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { customGroupsAPI, groupsAPI } from '../services/api';
import { pickBestSyncedGroupRecord } from '../utils/groupDisplay';
import Loader from '../components/Loader';

const { Title, Text } = Typography;

interface GroupRecord {
  _id: string;
  groupId?: string;
  name?: string;
  description?: string;
  participantsCount?: number;
  accountId?: { _id: string; accountName: string; phoneNumber?: string };
  updatedAt?: string;
}

interface GroupedGroupRow {
  groupKey: string;
  name: string;
  description: string;
  participantsCount: number;
  accounts: Array<{ _id: string; accountName: string }>;
  records: GroupRecord[];
  freshestRecord: GroupRecord;
}

interface CustomGroupData {
  _id: string;
  name: string;
  description: string;
  attachedGroups: Array<{ _id: string; name: string; [key: string]: any }>;
}

const EditCustomGroup: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [customGroup, setCustomGroup] = useState<CustomGroupData | null>(null);
  const [allGroups, setAllGroups] = useState<GroupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [groupSearchText, setGroupSearchText] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  useEffect(() => {
    if (!id) {
      navigate('/custom-groups');
      return;
    }
    const fetch = async () => {
      try {
        setLoading(true);
        const [groupRes, groupsRes] = await Promise.all([
          customGroupsAPI.getById(id),
          groupsAPI.getAll(),
        ]);
        setCustomGroup(groupRes.data);
        setAllGroups(groupsRes.data || []);
        const g = groupRes.data;
        if (g) {
          form.setFieldsValue({
            name: g.name,
            description: g.description || '',
          });
          setSelectedGroupIds((g.attachedGroups || []).map((ag: { _id: string }) => ag._id));
        }
      } catch (err: any) {
        message.error(err.response?.data?.error || 'Failed to load custom group');
        navigate('/custom-groups');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, navigate, form]);

  const groupedGroupsList = useMemo((): GroupedGroupRow[] => {
    if (!allGroups.length) return [];
    const byKey = new Map<string, GroupRecord[]>();
    for (const g of allGroups) {
      const key = g.groupId || g._id;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key)!.push(g);
    }
    return Array.from(byKey.entries()).map(([groupKey, records]) => {
      const displayRecord = pickBestSyncedGroupRecord(records);
      const accounts = records.map((r) => ({
        _id: r.accountId?._id ?? '',
        accountName: r.accountId?.accountName ?? 'Unknown',
      }));
      return {
        groupKey,
        name: displayRecord.name ?? 'Unnamed Group',
        description: displayRecord.description ?? '',
        participantsCount: displayRecord.participantsCount ?? 0,
        accounts,
        records,
        freshestRecord: displayRecord,
      };
    });
  }, [allGroups]);

  const filteredGroupsForSelect = useMemo(() => {
    if (!groupSearchText) return groupedGroupsList;
    const searchLower = groupSearchText.toLowerCase();
    return groupedGroupsList.filter(
      (row) =>
        row.name.toLowerCase().includes(searchLower) ||
        row.accounts.some((a) => a.accountName.toLowerCase().includes(searchLower))
    );
  }, [groupedGroupsList, groupSearchText]);

  const selectedRowKeys = useMemo(
    () =>
      groupedGroupsList
        .filter((row) => row.records.some((r) => selectedGroupIds.includes(r._id)))
        .map((row) => row.groupKey),
    [groupedGroupsList, selectedGroupIds]
  );

  const selectedGroupsSummary = useMemo(() => {
    const selected = groupedGroupsList.filter((row) => selectedRowKeys.includes(row.groupKey));
    if (selected.length === 0) return '';
    return selected
      .map((r) => `${r.name} (${r.accounts.map((a) => a.accountName).join(', ')})`)
      .join('\n');
  }, [groupedGroupsList, selectedRowKeys]);

  const onSelectionChange = useCallback(
    (_keys: React.Key[], rows: GroupedGroupRow[]) => {
      const idsFromVisibleSelection = rows.map((r) => r.freshestRecord._id);
      const currentValue = selectedGroupIds;
      const idsFromHidden = currentValue.filter((groupId) => {
        const row = groupedGroupsList.find((r) => r.records.some((rec) => rec._id === groupId));
        return !row;
      });
      setSelectedGroupIds([...idsFromHidden, ...idsFromVisibleSelection]);
    },
    [groupedGroupsList, selectedGroupIds]
  );

  const handleSubmit = async (values: { name: string; description?: string }) => {
    if (!id) return;
    if (selectedGroupIds.length === 0) {
      message.warning('Please select at least one group');
      return;
    }
    try {
      setSubmitting(true);
      const idToKey = new Map<string, string>();
      allGroups.forEach((g) => {
        idToKey.set(g._id, g.groupId || g._id);
      });
      const keyList = selectedGroupIds.map((groupId) => idToKey.get(groupId)).filter(Boolean) as string[];
      const selectedKeys = Array.from(new Set(keyList));
      const freshestByKey: Record<string, string> = {};
      groupedGroupsList.forEach((r) => {
        freshestByKey[r.groupKey] = r.freshestRecord._id;
      });
      const attachedGroups = selectedKeys.map((k) => freshestByKey[k]).filter(Boolean);

      await customGroupsAPI.update(id, {
        name: values.name,
        description: values.description || '',
        attachedGroups,
      });
      message.success('Custom group updated successfully');
      navigate('/custom-groups');
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to update custom group');
    } finally {
      setSubmitting(false);
    }
  };

  const tableColumns = [
    {
      title: 'Group Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text || 'Unnamed Group'}</Text>,
    },
    {
      title: 'Account(s)',
      key: 'accounts',
      render: (_: unknown, record: GroupedGroupRow) => (
        <Space size={[4, 4]} wrap>
          {record.accounts.map((ac) => (
            <Tag key={ac._id} color="blue">
              {ac.accountName}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Members',
      dataIndex: 'participantsCount',
      key: 'participantsCount',
      width: 100,
      render: (n: number) => <Tag color="#0e4842">{n}</Tag>,
    },
  ];

  if (loading || !customGroup) {
    return <Loader fullScreen text="Loading..." />;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/custom-groups')}
          >
            Back
          </Button>
          <Title level={2} className="mb-0">
            Edit Custom Group
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ name: customGroup.name, description: customGroup.description || '' }}
        >
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Card
                title="Select WhatsApp Groups"
                className="h-full"
                bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%' }}
              >
                <div className="mb-3">
                  <Input
                    placeholder="Search groups by name or account..."
                    value={groupSearchText}
                    onChange={(e) => setGroupSearchText(e.target.value)}
                    allowClear
                    prefix={<SearchOutlined />}
                    size="large"
                  />
                </div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                  Same group on multiple accounts is shown once. Select groups using the checkboxes.
                </Text>
                {allGroups.length === 0 ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: 'center',
                      background: '#fafafa',
                      borderRadius: 8,
                    }}
                  >
                    <Text type="secondary">
                      No WhatsApp groups available. Please sync accounts first.
                    </Text>
                  </div>
                ) : groupSearchText && filteredGroupsForSelect.length === 0 ? (
                  <div
                    style={{
                      padding: 24,
                      textAlign: 'center',
                      background: '#fafafa',
                      borderRadius: 8,
                    }}
                  >
                    <Text type="secondary">No groups found matching &quot;{groupSearchText}&quot;</Text>
                  </div>
                ) : (
                  <Table
                    size="small"
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys,
                      onChange: onSelectionChange,
                    }}
                    columns={tableColumns}
                    dataSource={filteredGroupsForSelect}
                    rowKey="groupKey"
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (t) => `${t} groups`,
                    }}
                    scroll={{ y: 400 }}
                  />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title="Custom Group Details" className="h-full">
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: 'Please enter a name' }]}
                >
                  <Input placeholder="Enter custom group name" size="large" />
                </Form.Item>

                <Form.Item name="description" label="Description">
                  <Input.TextArea
                    placeholder="Enter description (optional)"
                    rows={3}
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="Selected Groups"
                  required
                  validateStatus={selectedGroupIds.length === 0 ? 'error' : undefined}
                  help={
                    selectedGroupIds.length === 0
                      ? 'Please select at least one group from the left panel'
                      : undefined
                  }
                >
                  <Input.TextArea
                    readOnly
                    rows={8}
                    value={selectedGroupsSummary}
                    placeholder="No groups selected. Use the checkboxes on the left to select groups."
                    style={{
                      width: '100%',
                      resize: 'none',
                      background: selectedGroupsSummary ? '#fafafa' : undefined,
                    }}
                  />
                </Form.Item>

                <Form.Item className="mb-0">
                  <Space size="middle">
                    <Button
                      size="large"
                      onClick={() => navigate('/custom-groups')}
                      style={{ minWidth: 140 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      htmlType="submit"
                      loading={submitting}
                      disabled={selectedGroupIds.length === 0}
                      style={{
                        color: '#fff',
                        minWidth: 180,
                      }}
                    >
                      Update Custom Group
                    </Button>
                  </Space>
                </Form.Item>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default EditCustomGroup;
